import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';

import type { IResourceClientMember, IResourceDocumentItem } from '@/api/types';
import type { ITreeFolder } from '@/components/common';
import type { IDocumentEditForm } from './types';
import type { IDateFormatter } from '@/types/date';

import { Button, Input, Textarea } from '@/components/common';
import { createFileProxyFromPath, formData, mapFolderTreeToBasicClientFolder } from '@/helpers';
import { FolderSelect, FileInput, ClientMemberSelect } from '@/components/common/form';
import { ATTACHMENT_SIZE_LIMIT, DATE_ISO_FORMAT, TIME_ISO_FORMAT } from '@/config/variables';
import { documentEditSchema } from './modalSchemas';
import { useTimezone } from '@/hooks';
import { DateTimeInput } from '@/components/common/form/DateTimeInput';
import { ResourceMetadataSection } from '@/components/common/resources/modals';

const defaultValues = (parentFolderSignature: string, userId: number): IDocumentEditForm => ({
  folder: parentFolderSignature,
  name: '',
  description: '',
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  file: [],
  createdBy: userId,
});

const mapResourceToFormValues = (
  resource: IResourceDocumentItem,
  parentFolderSignature: string,
  formatDate: IDateFormatter,
): IDocumentEditForm => {
  const file = createFileProxyFromPath(resource.path);

  return {
    folder: parentFolderSignature,
    name: resource.name,
    description: resource.description ?? '',
    startDate: formatDate(resource.dateStart, DATE_ISO_FORMAT),
    startTime: formatDate(resource.dateStart, TIME_ISO_FORMAT),
    endDate: resource.dateEnd ? formatDate(resource.dateEnd, DATE_ISO_FORMAT) : '',
    endTime: resource.dateEnd ? formatDate(resource.dateEnd, TIME_ISO_FORMAT) : '',
    file,
    createdBy: resource.createdBy,
  };
};

interface IDocumentFormProps {
  onSubmit: (data: IDocumentEditForm) => void;
  onCancel: () => void;
  existingResource?: IResourceDocumentItem;
  foldersList: ITreeFolder[];
  clientId: number;
  parentFolderId: number;
  clientMemberList: IResourceClientMember[];
  userId: number;
}

const DocumentForm = ({
  onSubmit,
  onCancel,
  existingResource,
  foldersList,
  clientId,
  parentFolderId,
  clientMemberList,
  userId,
}: IDocumentFormProps): React.ReactElement => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const folderOptions = useMemo(
    () => mapFolderTreeToBasicClientFolder(foldersList, 'document', clientId),
    [foldersList, clientId],
  );
  const parentFolderSignature = useMemo(
    () => JSON.stringify({ id: parentFolderId, clientId }),
    [clientId, parentFolderId],
  );
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, touchedFields },
  } = useForm<IDocumentEditForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(documentEditSchema),
    defaultValues: existingResource
      ? mapResourceToFormValues(existingResource, parentFolderSignature, formatDateTime)
      : defaultValues(parentFolderSignature, userId),
  });

  useEffect(() => {
    setValue('folder', parentFolderSignature);
  }, [parentFolderSignature, setValue]);

  useEffect(() => {
    if (existingResource) {
      reset(mapResourceToFormValues(existingResource, parentFolderSignature, formatDateTime));
    }
  }, [existingResource, formatDateTime, parentFolderSignature, reset]);

  const formDataOptions = {
    register,
    touchedFields,
    errors,
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FolderSelect
        name="folder"
        folders={folderOptions}
        label={t`resources.modals.folder`}
        {...formData('folder', formDataOptions)}
      />
      <Input
        name="name"
        type="text"
        label={t`resources.modals.name`}
        required
        autoFocus
        {...formData('name', formDataOptions)}
      />
      <Textarea
        name="description"
        label={t`resources.modals.description`}
        control={control}
        maxLength={500}
        resizeVertical
      />
      <Controller
        name="file"
        control={control}
        render={({ field, fieldState }) => (
          <FileInput
            label={t`resources.modals.file`}
            name="file"
            value={field.value}
            error={fieldState.error}
            setValue={files => setValue('file', files)}
            maxFiles={1}
            maxSize={ATTACHMENT_SIZE_LIMIT}
            required
          />
        )}
      />
      <Row>
        <Col xs={12} xl={6}>
          <DateTimeInput
            dateName="startDate"
            timeName="startTime"
            formDataOptions={formDataOptions}
            label={t`resources.modals.publicationDate`}
            required
          />
        </Col>
        <Col xs={12} xl={6}>
          <DateTimeInput
            dateName="endDate"
            timeName="endTime"
            formDataOptions={formDataOptions}
            label={t`resources.modals.expirationDate`}
          />
        </Col>
      </Row>
      <ClientMemberSelect
        name="createdBy"
        label={t`resources.modals.createdBy`}
        clientMemberList={clientMemberList}
        metaData={existingResource}
        {...formData('createdBy', formDataOptions)}
      />
      <ResourceMetadataSection item={existingResource} />
      <FormControl>
        <StyledButton color="blueOutline" className="me-3" onClick={onCancel}>
          {t`common.btnCancel`}
        </StyledButton>
        <StyledButton color="blue" type="submit">
          {existingResource ? t`common.btnSave` : t`common.btnCreate`}
        </StyledButton>
      </FormControl>
    </form>
  );
};

const StyledButton = styled(Button)`
  width: 190px;
`;

const FormControl = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

export { DocumentForm };
